/**
 * The typing indicator's rules, pure so they are unit-tested on their own;
 * useChatTyping wires them to ant (the chatTyping mutation and subscription).
 *
 * Sending: at most one chatTyping per 3 s while the agent types (ant drops
 * anything within 2 s of the previous one anyway). Showing: a typer is shown
 * for 6 s after their last event, or until a message from them lands, and
 * the agent's own events (ant echoes them back) are never shown.
 */

import { ChatTypingEvent } from './socket';

/** An agent types: tell ant at most this often. */
export const TYPING_SEND_INTERVAL_MS = 3000;
/** A typer disappears this long after their last event. */
export const TYPING_EXPIRY_MS = 6000;

export interface Throttle {
  /** Runs the task unless it ran less than the interval ago; true when it ran. */
  notify: () => boolean;
  /** Forgets the last run, so the next notify runs at once. */
  reset: () => void;
}

/** A leading-edge throttle: the first call runs, then one per `intervalMs`. */
export function createThrottle(
  task: () => void,
  intervalMs: number = TYPING_SEND_INTERVAL_MS,
  now: () => number = Date.now
): Throttle {
  let last: number | null = null;
  return {
    notify: () => {
      const t = now();
      if (last !== null && t - last < intervalMs) return false;
      last = t;
      task();
      return true;
    },
    reset: () => {
      last = null;
    },
  };
}

export interface Typer {
  senderType: 'user' | 'admin';
  senderId: string;
  name: string | null;
  /** Local clock (ms): the server's `at` may be skewed against this browser. */
  expiresAt: number;
}

/** Who is typing, by `${senderType}:${senderId}`. */
export type TypingState = Record<string, Typer>;

export const EMPTY_TYPING: TypingState = {};

/**
 * A typing event folded in. Ignored when it is for another conversation,
 * of an unknown sender type, or the agent's own: with `myAdminId` unknown
 * no agent's event is shown, since this agent's own could not be told apart.
 */
export function addTypingEvent(
  state: TypingState,
  event: ChatTypingEvent,
  options: { conversationId: string; myAdminId: string | null; now: number }
): TypingState {
  if (event.conversationId.toLowerCase() !== options.conversationId.toLowerCase()) {
    return state;
  }
  const senderType = event.senderType.toLowerCase();
  if (senderType !== 'user' && senderType !== 'admin') return state;
  if (
    senderType === 'admin' &&
    (!options.myAdminId || event.senderId === options.myAdminId)
  ) {
    return state;
  }
  const key = `${senderType}:${event.senderId}`;
  return {
    ...state,
    [key]: {
      senderType,
      senderId: event.senderId,
      name: event.name?.trim() || null,
      expiresAt: options.now + TYPING_EXPIRY_MS,
    },
  };
}

/** Without the typers whose 6 s are up; the same object when none are. */
export function expireTypers(state: TypingState, now: number): TypingState {
  const keys = Object.keys(state);
  const kept = keys.filter((k) => state[k].expiresAt > now);
  if (kept.length === keys.length) return state;
  const out: TypingState = {};
  kept.forEach((k) => {
    out[k] = state[k];
  });
  return out;
}

/** Without `senderId` (their message landed); the same object when absent. */
export function clearTyper(state: TypingState, senderId: string): TypingState {
  const keys = Object.keys(state).filter((k) => state[k].senderId === senderId);
  if (keys.length === 0) return state;
  const out = { ...state };
  keys.forEach((k) => delete out[k]);
  return out;
}

/** When the next typer expires (ms), or null with nobody typing. */
export function nextExpiry(state: TypingState): number | null {
  const times = Object.values(state).map((t) => t.expiresAt);
  return times.length ? Math.min(...times) : null;
}

const joinNames = (names: string[]): string =>
  names.length <= 1
    ? names[0] ?? ''
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

export interface TypingLabels {
  /** "Ada is typing…", or null. */
  rider: string | null;
  /** "Ana R. is also typing a reply" (a collision), or null. */
  agents: string | null;
}

/** What the thread shows under the last message. */
export function typingLabels(state: TypingState): TypingLabels {
  const typers = Object.values(state);
  const rider = typers.find((t) => t.senderType === 'user');
  const agents = typers
    .filter((t) => t.senderType === 'admin')
    .map((t) => t.name || 'Another agent');
  return {
    rider: rider ? `${rider.name || 'The rider'} is typing…` : null,
    agents: agents.length
      ? `${joinNames(agents)} ${agents.length === 1 ? 'is' : 'are'} also typing a reply`
      : null,
  };
}
